import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../common/helpers/cn';

/**
 * shadcn/ui Button, ported from student-hub for hub's v2 migration.
 * Tailwind v4 -> v3.3 conversions applied: `size-*` -> `h-* w-*`,
 * `shadow-xs` -> `shadow-sm`, `has-*` variants dropped (no v3 support).
 *
 * Wrapped in React.forwardRef: student-hub runs React 19 (ref-as-prop), but hub
 * is on React 18 where a plain function component drops `ref`. Radix needs the
 * ref to flow to the DOM node when Button is used as an `asChild` trigger
 * (e.g. DropdownMenuTrigger) so the popper has an anchor to position against.
 */
const buttonVariants = cva(
  "disabled:text-grey-400 disabled:bg-grey-200 hover:cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-3xl text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='h-'])]:h-4 [&_svg:not([class*='w-'])]:w-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20',
        outline: 'border bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        green: 'bg-green-300 border-green-300 border text-secondary-foreground hover:bg-green-300/90',
        grey: 'bg-gray-400 border-gray-400 border text-primary-foreground hover:bg-gray-400/90',
        outlineGrey: 'bg-white border-gray-400 border text-gray-400 hover:bg-gray-400/90 hover:text-primary-foreground',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md gap-1.5 px-3',
        lg: 'h-10 rounded-md px-6',
        icon: 'h-9 w-9',
        'icon-sm': 'h-8 w-8',
        'icon-lg': 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
    }
>(function Button({ className, variant, size, asChild = false, ...props }, ref) {
  const Comp: React.ElementType = asChild ? Slot : 'button';

  return <Comp ref={ref} data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
});

// eslint-disable-next-line
export { Button, buttonVariants };
